/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as MsDyn365 from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IAlertProps, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { CookieContext } from '@msdyn365-commerce/runtime-internal/dist/types/utils/cookie-utils';
import classnames from 'classnames';
import * as React from 'react';
import * as GTM from '../../shared-utils/google-tag-manager/gtm';

import { CookieComplianceAcceptButton, CookieComplianceLinks } from './components';
import { IDlvryCookieComplianceConfig, IDlvryCookieComplianceProps } from './dlvry-cookie-compliance.props.autogenerated';
import { CookieComplianceAcceptEssentialButton } from './components/accept-essential-button';
import { IDlvryCookieComplianceData } from './dlvry-cookie-compliance.data';
import { ICookieContextWithEssential } from '../../shared-utils/cookie-utilities';

/**
 * Alert state interface.
 */
export interface IAlertState {
    visible: boolean;
}

/**
 * Cookie compliance view props interface.
 */
export interface ICookieComplianceViewProps extends IDlvryCookieComplianceProps<{}> {
    CookieComplianceBanner: IModuleProps;
    AlertProps: IAlertProps;
    Content: INodeProps;
    ButtonContainer: INodeProps;
    text?: React.ReactNode;
    links?: React.ReactNode;
    acceptButton?: React.ReactNode;
    acceptEssentialButton?: React.ReactNode;
    className: string;
    onDismiss(): void;
}

/**
 *
 * CookieCompliance component.
 * @extends {React.PureComponent<IDlvryCookieComplianceProps<IHeroConfig>>}
 */
class CookieCompliance extends React.PureComponent<IDlvryCookieComplianceProps<IDlvryCookieComplianceData>, IAlertState> {
    private readonly cookieRef: React.RefObject<HTMLInputElement>;

    private readonly hideBannerCookieName = '_msdyn365__hcb2_';

    private readonly telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    public constructor(props: IDlvryCookieComplianceProps<IDlvryCookieComplianceData>) {
        super(props);
        this.state = { visible: false };
        this.cookieRef = React.createRef();
        this._onDismiss = this._onDismiss.bind(this);
    }

    public componentDidMount(): void {
        const { context } = this.props;
        const cookies = context.request.cookies as ICookieContextWithEssential;
        const hideCookieBannerCookie = !!cookies.get(this.hideBannerCookieName, true).value;

        // Check if cookie compliance is disabled. If yes we can give consent to Clarity and GTM without showing the banner.
        if (!cookies._options.isConsentRequired) {
            if (this.props.config.clarity) {
                this.clarityConsent();
                this.claritySetIds();
            }

            if (this.props.config.googleTagManager) {
                GTM.consentAll();
            }
            return;
        }

        if (!hideCookieBannerCookie) {
            context.request.cookies.deleteConsentCookie();

            if (this.props.config.clarity) {
                this.clarityRevokeConsent();
            }

            if (this.props.config.googleTagManager) {
                GTM.consentNone();
            }

            this.setState({ visible: true });
        } else if (context.request.cookies.isConsentGiven()) {
            try {
                if (this.props.config.clarity) {
                    this.claritySetIds();
                }

                if (this.props.config.googleTagManager && window && localStorage.getItem('consentMode') === null) {
                    // User has consented to all cookies
                    // but GTM will be all denied because user didn't get the popup while GTM support was in.
                    // In this case we should be fine to give full consent to GTM since user has consented to all cookies.
                    GTM.consentAll();
                }
            } catch {
                // Do nothing
            }
        }
    }

    public render(): JSX.Element | null {
        const { config, resources, telemetry } = this.props;
        const {
            acceptCookiesButtonText,
            acceptCookiesAriaLabel,
            acceptEssentialCookiesButtonText,
            acceptEssentialCookiesAriaLabel
        } = resources;
        const { visible } = this.state;

        let moduleConfig: IDlvryCookieComplianceConfig;

        try {
            moduleConfig = config;
        } catch (error) {
            telemetry.error(`Something went wrong while rendering the alert module ------${error}`);
            return <div>{error}</div>;
        }

        if (!visible) {
            return <></>;
        }

        if (!MsDyn365.msdyn365Commerce.isBrowser) {
            return <></>;
        }

        const viewProps: Partial<ICookieComplianceViewProps> = {
            ...this.props,
            // onDismiss: this._onDismiss,
            CookieComplianceBanner: {
                moduleProps: this.props,
                className: classnames('ms-cookie-compliance', moduleConfig.className),
                ref: this.cookieRef
            },
            AlertProps: {
                className: 'ms-cookie-compliance__container',
                color: 'white',
                fade: false
            },
            Content: {
                className: 'ms-cookie-compliance__content'
            },
            ButtonContainer: {
                className: 'ms-cookie-compliance__button-container'
            },
            text: moduleConfig.content && (
                <MsDyn365.RichTextComponent
                    className='ms-cookie-compliance__text'
                    text={moduleConfig.content}
                    editProps={{ onEdit: this.handleContentChange, requestContext: this.props.context.request }}
                />
            ),
            links: ArrayExtensions.hasElements(moduleConfig.actionLinks) && (
                <CookieComplianceLinks
                    links={moduleConfig.actionLinks}
                    onTextChange={this.handleLinkTextChange}
                    requestContext={this.props.context.request}
                    telemetryContent={this.telemetryContent}
                />
            ),
            acceptButton: (
                <CookieComplianceAcceptButton
                    onClose={() => this._onDismiss(true)}
                    acceptCookiesButtonText={acceptCookiesButtonText}
                    acceptCookiesButtonAriaLabel={acceptCookiesAriaLabel}
                    telemetryContent={this.telemetryContent}
                />
            ),
            acceptEssentialButton: (
                <CookieComplianceAcceptEssentialButton
                    onClose={() => this._onDismiss(false)}
                    acceptEssentialCookiesButtonText={acceptEssentialCookiesButtonText}
                    acceptEssentialCookiesButtonAriaLabel={acceptEssentialCookiesAriaLabel}
                    telemetryContent={this.telemetryContent}
                />
            )
        };

        return this.props.renderView(viewProps as ICookieComplianceViewProps) as React.ReactElement;
    }

    /**
     * Handles Link content change event.
     * @param linkIndex - Index of link in linkarray.
     * @returns Void.
     */
    public handleLinkTextChange = (linkIndex: number) => (event: MsDyn365.ContentEditableEvent): void => {
        if (this.props.config.actionLinks?.[linkIndex]) {
            this.props.config.actionLinks[linkIndex].linkText = event.target.value;
        }
    };

    /**
     * Handles content change event.
     * @param event - Event.
     */
    public handleContentChange = (event: MsDyn365.ContentEditableEvent): void => {
        this.props.config.content = event.target.value;
    };

    private _onDismiss(consentAll: boolean): void {
        const { context, config } = this.props;
        const cookies = context.request.cookies as CookieContext;

        // Set cookie to hide the banner
        cookies.set(this.hideBannerCookieName, Date.now(), { path: '/', maxAge: 31536000, isEssential: true });

        if (consentAll && cookies) {
            cookies.setConsentCookie();

            if (config.clarity) {
                this.clarityConsent();
                this.claritySetIds();
            }

            if (this.props.config.googleTagManager) {
                GTM.consentAll();
            }

            location.reload();
        }

        if (!consentAll && cookies) {
            if (config.clarity) {
                this.clarityRevokeConsent();
            }

            if (this.props.config.googleTagManager) {
                GTM.consentNone();
            }
        }

        this.setState({ visible: false });
    }

    private clarityConsent = (): void => {
        if (typeof window === 'undefined' || !(window as any).clarity) {
            return;
        }

        try {
            // Tell Clarity that the user has consented to all cookies
            (window as any).clarity('consent');
        } catch (error) {
            console.error('Error while setting clarity consent cookie', error);
        }
    };

    private clarityRevokeConsent = (): void => {
        if (typeof window === 'undefined' || !(window as any).clarity) {
            return;
        }

        try {
            // Tell Clarity that the user has revoked consent to all cookies
            (window as any).clarity('consent', false);
        } catch (error) {
            console.error('Error while revoking clarity consent cookie', error);
        }
    };

    private claritySetIds = (): void => {
        if (typeof window === 'undefined' || !(window as any).clarity) {
            return;
        }

        try {
            const { customerInformation, organizationInformation } = this.props.data;

            if (customerInformation.result?.AccountNumber) {
                (window as any).clarity('set', 'customerId', customerInformation.result.AccountNumber);
            }

            if (organizationInformation.result?.AccountNumber) {
                (window as any).clarity('set', 'organizationCustomerId', organizationInformation.result.AccountNumber);
            }
        } catch (error) {
            console.error('Error while setting clarity ids', error);
        }
    };
}

export default CookieCompliance;
